import { useCallback, useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { instructorAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Field, Modal, btnGhost, btnPrimary, inputCls } from '../../components/ui';
import {
  DataTable, EmptyPanel, PageHeader, StatCards, TableRow, Thumb, adminBtnPrimary, adminCard, fmtDate, money,
  statusBadge,
} from '../../components/adminUi';

// Admin 9 · Earnings — the instructor's view (s134 redesign): Request withdrawal,
// three stat cards (Available highlighted), Sales + Withdrawals tables, and a
// "How payouts work" explainer.

export default function EarningsPage() {
  const [balance, setBalance] = useState(null);
  const [sales, setSales] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [asking, setAsking] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    instructorAPI.balance().then(({ data }) => setBalance(data)).catch(() => setBalance({ earned: 0, committed: 0, available: 0 }));
    instructorAPI.sales().then(({ data }) => setSales(data.sales)).catch(() => {});
    instructorAPI.withdrawals().then(({ data }) => setWithdrawals(data.withdrawals)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await instructorAPI.requestWithdrawal({ amount: parseFloat(amount), method });
      toast.success(t('Withdrawal requested'));
      setAsking(false);
      setAmount('');
      setMethod('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!balance) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;

  const SALE_COLS = [
    { label: t('Course'), width: 'minmax(0,2.4fr)' },
    { label: t('Sale amount'), width: '130px' },
    { label: t('Your share'), width: '130px' },
    { label: t('Date'), width: '120px' },
  ];
  const W_COLS = [
    { label: t('Amount'), width: '140px' },
    { label: t('Method'), width: 'minmax(0,1.6fr)' },
    { label: t('Status'), width: '120px' },
    { label: t('Requested'), width: '120px' },
  ];

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader title={t('Earnings')} subtitle={t('Your share of every sale of your courses.')}>
        <button type="button" onClick={() => setAsking(true)} disabled={balance.available <= 0} className={adminBtnPrimary}>
          <Wallet size={16} /> {t('Request withdrawal')}
        </button>
      </PageHeader>

      <StatCards
        items={[
          { label: t('Total earned'), value: money(balance.earned), note: t('your share of all sales') },
          { label: t('Withdrawn / pending'), value: money(balance.committed), note: t('paid out or requested') },
          { label: t('Available'), value: money(balance.available), note: t('ready to withdraw'), accent: true },
        ]}
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr),320px]">
        <div className="flex min-w-0 flex-col gap-6">
          <section className="flex flex-col gap-2.5">
            <h2 className="text-lg font-bold text-lms-ink">{t('Sales')}</h2>
            <DataTable cols={SALE_COLS} empty={sales.length === 0 ? <EmptyPanel bare title={t('No sales yet.')}>{t('Each sale of one of your courses shows up here with your share.')}</EmptyPanel> : null}>
              {sales.map((sale) => (
                <TableRow key={sale.id} cols={SALE_COLS}>
                  <div className="flex min-w-0 items-center gap-3">
                    <Thumb src={sale.course_thumbnail} title={sale.course_title} />
                    <span className="truncate text-sm text-lms-ink">{sale.course_title || '—'}</span>
                  </div>
                  <span className="text-[13.5px] text-lms-sub lms-num">{money(sale.amount)}</span>
                  <span className="text-[14.5px] font-bold text-lms-free lms-num">{money(sale.instructor_revenue)}</span>
                  <span className="text-[13px] text-lms-sub">{fmtDate(sale.created_at)}</span>
                </TableRow>
              ))}
            </DataTable>
          </section>

          <section className="flex flex-col gap-2.5">
            <h2 className="text-lg font-bold text-lms-ink">{t('Withdrawals')}</h2>
            <DataTable cols={W_COLS} empty={withdrawals.length === 0 ? <EmptyPanel bare title={t('No withdrawal requests yet.')} /> : null}>
              {withdrawals.map((w) => (
                <TableRow key={w.id} cols={W_COLS}>
                  <span className="text-[14.5px] font-bold text-lms-ink lms-num">{money(w.amount)}</span>
                  <span className="truncate text-[13.5px] text-lms-body">{w.method || '—'}</span>
                  {statusBadge(w.status_label || 'pending')}
                  <span className="text-[13px] text-lms-sub">{fmtDate(w.requested_at)}</span>
                </TableRow>
              ))}
            </DataTable>
          </section>
        </div>

        <aside className={`${adminCard} flex flex-col gap-4 p-5 sm:px-6`}>
          <h2 className="text-base font-bold text-lms-ink">{t('How payouts work')}</h2>
          <ol className="flex list-decimal flex-col gap-2.5 pl-5 text-sm leading-relaxed text-lms-body">
            <li>{t('Request a withdrawal of your available balance and say how you want to be paid.')}</li>
            <li>{t('The admin sends the money by that method.')}</li>
            <li>{t('The request is marked Paid and leaves your balance.')}</li>
          </ol>
        </aside>
      </div>

      {asking && (
        <Modal title={t('Request withdrawal')} onClose={() => setAsking(false)}>
          <form onSubmit={request} className="space-y-4">
            <Field label={`${t('Amount')} (${t('Available')}: $${balance.available.toFixed(2)})`}>
              <input type="number" min="0.01" max={balance.available} step="0.01" value={amount}
                onChange={(e) => setAmount(e.target.value)} required autoFocus className={inputCls} />
            </Field>
            <Field label={t('Payout method / details')} hint={t('e.g. bank transfer, PayPal email — how you want to be paid.')}>
              <input value={method} onChange={(e) => setMethod(e.target.value)} required className={inputCls} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAsking(false)} className={btnGhost}>{t('Cancel')}</button>
              <button type="submit" disabled={busy} className={btnPrimary}>
                {busy ? t('Submitting…') : t('Request')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
